import React from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, Clock, MapPin, Facebook } from "lucide-react";
import { Eyebrow } from "./Reveal";
import { useContent } from "../ContentContext";

export default function Contact({ onEstimate }) {
  const { company } = useContent();
  return (
    <>
      <section id="contact" data-testid="contact-section" className="py-24 md:py-32" style={{ background: "var(--bone)" }}>
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-2 gap-14">
          <div>
            <Eyebrow>Contact</Eyebrow>
            <h2 className="font-display font-extrabold uppercase tracking-tight text-4xl sm:text-5xl leading-[0.95]"
                style={{ color: "var(--pine)" }}>
              Let&apos;s get <span className="font-serif-a italic normal-case font-medium" style={{ color: "var(--brass)" }}>started</span>
            </h2>
            <p className="mt-5 max-w-md leading-relaxed" style={{ color: "#4a4a4a" }}>
              Free estimates.{company.licensed_insured ? " Licensed & insured." : ""}{company.emergency_247 ? " 24/7 emergency service." : ""} Reach out today.
            </p>
            <div className="mt-8 space-y-4">
              <a href={`tel:${company.phoneRaw}`} data-testid="contact-phone" className="flex items-center gap-4 group">
                <span className="w-11 h-11 rounded-full flex items-center justify-center" style={{ background: "var(--pine)" }}><Phone size={18} style={{ color: "var(--brass)" }} /></span>
                <span className="font-display font-bold text-lg" style={{ color: "var(--pine)" }}>{company.phone}</span>
              </a>
              <div className="flex items-center gap-4">
                <span className="w-11 h-11 rounded-full flex items-center justify-center" style={{ background: "var(--pine)" }}><Mail size={18} style={{ color: "var(--brass)" }} /></span>
                <span style={{ color: "var(--pine)" }}>Send a request through our form</span>
              </div>
              <div className="flex items-center gap-4">
                <span className="w-11 h-11 rounded-full flex items-center justify-center" style={{ background: "var(--pine)" }}><Clock size={18} style={{ color: "var(--brass)" }} /></span>
                <span style={{ color: "var(--pine)" }}>{company.hours}</span>
              </div>
              <div className="flex items-center gap-4">
                <span className="w-11 h-11 rounded-full flex items-center justify-center" style={{ background: "var(--pine)" }}><MapPin size={18} style={{ color: "var(--brass)" }} /></span>
                <span style={{ color: "var(--pine)" }}>Huntsville, North Alabama &amp; Tennessee</span>
              </div>
            </div>
          </div>
          <div className="rounded-3xl p-8 md:p-10 noise relative" style={{ background: "var(--pine)" }}>
            <div className="relative z-10">
              <h3 className="font-display font-bold uppercase text-2xl" style={{ color: "var(--moss)" }}>Request a free estimate</h3>
              <p className="mt-3 mb-6" style={{ color: "rgba(240,235,225,0.75)" }}>Tell us about your project — most requests get a same-day response.</p>
              <button data-testid="contact-estimate-btn" onClick={onEstimate}
                      className="w-full rounded-full py-4 font-semibold transition-transform hover:scale-[1.02]"
                      style={{ background: "var(--sun)", color: "var(--pine)" }}>Open the request form</button>
              <a href={`tel:${company.phoneRaw}`} className="mt-4 block text-center font-semibold" style={{ color: "var(--brass)" }}>
                Prefer to call? {company.phone}
              </a>
            </div>
          </div>
        </div>
      </section>

      <footer data-testid="footer" className="pt-16 pb-24 sm:pb-8 noise relative" style={{ background: "var(--pine)" }}>
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <div className="grid md:grid-cols-4 gap-10 pb-12 border-b" style={{ borderColor: "rgba(240,235,225,0.12)" }}>
            <div className="md:col-span-2">
              <div className="font-display font-extrabold text-2xl" style={{ color: "var(--moss)" }}>TOP OF THE LINE</div>
              <div className="font-serif-a italic text-lg" style={{ color: "var(--brass)" }}>tree services llc</div>
              <p className="mt-4 max-w-sm text-sm leading-relaxed" style={{ color: "rgba(240,235,225,0.7)" }}>{company.tagline}. {company.sub}</p>
              <a href={company.facebook} target="_blank" rel="noreferrer" className="mt-5 inline-flex w-10 h-10 rounded-full items-center justify-center" style={{ background: "rgba(240,235,225,0.1)" }}>
                <Facebook size={18} style={{ color: "var(--brass)" }} />
              </a>
            </div>
            <div>
              <div className="text-xs font-semibold tracking-[0.2em] uppercase mb-4" style={{ color: "var(--brass)" }}>Areas</div>
              <ul className="space-y-2 text-sm" style={{ color: "rgba(240,235,225,0.7)" }}>
                {(company.areas || []).slice(0, 6).map((a) => <li key={a}>{a}</li>)}
              </ul>
            </div>
            <div>
              <div className="text-xs font-semibold tracking-[0.2em] uppercase mb-4" style={{ color: "var(--brass)" }}>Contact</div>
              <a href={`tel:${company.phoneRaw}`} className="block text-sm font-semibold mb-2" style={{ color: "var(--moss)" }}>{company.phone}</a>
              <p className="text-sm" style={{ color: "rgba(240,235,225,0.7)" }}>{company.hours}</p>
            </div>
          </div>
          <div className="pt-6 flex flex-col sm:flex-row justify-between gap-3 text-xs" style={{ color: "rgba(240,235,225,0.55)" }}>
            <span>© {new Date().getFullYear()} {company.name}. All rights reserved.</span>
            <div className="flex gap-5">
              <Link to="/privacy" data-testid="footer-privacy" className="hover:text-white">Privacy Policy</Link>
              <Link to="/terms" data-testid="footer-terms" className="hover:text-white">Terms &amp; Conditions</Link>
              <Link to="/admin" data-testid="footer-admin" className="hover:text-white">Admin</Link>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
}
